import React, { useState, useEffect, useRef } from 'react'
import { FONT_BODY, FONT_HEAD } from './styles'
import SchoolDisclosure from './SchoolDisclosure'

// Public demo-booking page at /demo. No login, no Supabase, no writes.
// Fully self-contained like FeedbackSurvey — everything is scoped .dm-
// or inline so nothing can leak into or out of the other public pages.
// Its one job is the TidyCal link. CE promo codes are handled elsewhere.
//
// Every address, price, count and name below is hardcoded marketing copy.
// Nothing on this page reads ARMLS or any other API.

const BOOK = 'https://tidycal.com/danae3/20-minute-meeting'
const TARGET_PRICE = 615000

export default function DemoLanding() {
  const [isMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768)

  const demoCSS = `
    @keyframes dmBar{0%,100%{transform:scaleY(0.35)}50%{transform:scaleY(1)}}
    @keyframes dmPulse{0%,100%{opacity:1}50%{opacity:0.3}}
    @keyframes dmSweep{0%{transform:translateX(-100%)}100%{transform:translateX(420%)}}
    .dm-fade{transition:opacity .22s ease}
    @keyframes dmUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
    .dm-r{opacity:0}
    .dm-r.in{animation:dmUp .55s cubic-bezier(.2,.8,.2,1) forwards}
    @media (prefers-reduced-motion: reduce){
      .dm-bar,.dm-dot,.dm-sweep{animation:none!important}
      .dm-sweep{opacity:0}
      .dm-seg,.dm-conf{transition:none!important}
      .dm-r{opacity:1!important;animation:none!important}
    }
  `

  function goTo(id) {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const wrap = { maxWidth:'880px', margin:'0 auto', padding:isMobile?'0 18px':'0 24px' }

  const goldPill = { display:'inline-block',background:'#fdbf00',color:'#412402',border:'none',borderRadius:'999px',
    padding:'11px 24px',fontSize:'13px',fontWeight:600,fontFamily:FONT_BODY,cursor:'pointer',textDecoration:'none' }

  const sectionLabel = (n, t) => (
    <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'6px'}}>
      <span style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',fontFamily:FONT_HEAD}}>{n}</span>
      {t && <span style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.5px',color:'#999',fontFamily:FONT_HEAD}}>· {t}</span>}
    </div>
  )

  const sectionHead = t => (
    <div style={{fontSize:'20px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,letterSpacing:'-0.3px',marginBottom:'16px'}}>{t}</div>
  )

  // ── Strategist block: count-up + bucket fill, fired once on scroll-in ──
  const priceRef = useRef(null)
  const [priceIn, setPriceIn] = useState(false)
  const [price, setPrice] = useState(0)

  useEffect(() => {
    const el = priceRef.current
    if (!el || typeof IntersectionObserver === 'undefined') { setPriceIn(true); return }
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) { setPriceIn(true); io.disconnect() }
    }, { threshold: 0.3 })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    if (!priceIn) return
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce) { setPrice(TARGET_PRICE); return }
    let raf
    let start = null
    const step = ts => {
      if (start === null) start = ts
      const p = Math.min((ts - start) / 1400, 1)
      const eased = 1 - Math.pow(1 - p, 3)
      setPrice(Math.round(TARGET_PRICE * eased))
      if (p < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [priceIn])

  // ── Report card: assembles itself once, on scroll-in ──
  const reportRef = useRef(null)
  const [reportIn, setReportIn] = useState(false)
  useEffect(() => {
    const el = reportRef.current
    if (!el || typeof IntersectionObserver === 'undefined') { setReportIn(true); return }
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) { setReportIn(true); io.disconnect() }
    }, { threshold: 0.25 })
    io.observe(el)
    return () => io.disconnect()
  }, [])
  const rv = reportIn ? 'dm-r in' : 'dm-r'

  const BUCKETS = [
    { label:'14 closed',  w:32, bg:'#147efb', fg:'white' },
    { label:'7 pending',  w:16, bg:'#4a94e6', fg:'white' },
    { label:'11 active',  w:25, bg:'#85B7EB', fg:'#042C53' },
    { label:'12 UCB',     w:27, bg:'#B5D4F4', fg:'#042C53' },
  ]

  // ── Report language flip ──
  const [lang, setLang] = useState('en')
  const [fading, setFading] = useState(false)
  function flip(l) {
    if (l === lang) return
    setFading(true)
    setTimeout(() => { setLang(l); setFading(false) }, 220)
  }

  const today = new Date()
  const dateEn = today.toLocaleDateString('en-US', { month:'long', day:'numeric', year:'numeric' }).toUpperCase()
  const dateEs = today.toLocaleDateString('es-MX', { day:'numeric', month:'long', year:'numeric' }).toUpperCase()

  const T = {
    en: {
      eyebrow:'PRICING STRATEGY · PREPARED ' + dateEn,
      k1:'RECOMMENDED LIST PRICE', range:'Range', confLabel:'Confidence', conf:'High',
      how:'HOW WE GOT THERE',
      lensA:'Appraisal lens',
      lensAtext:'Adjusted for square footage, lot, year built and condition against fourteen closed sales.',
      lensB:'Broker lens',
      lensBtext:'Seven pending and twelve UCB say this pocket is moving. Everything over $632,000 sat 41 days.',
      obj:'"Why not $650,000?"',
      objText:'Three homes tried it in this pocket since March. Two reduced within six weeks; one is still sitting. Overpricing here costs about $9,000 in carry and a weaker second look.',
      role:'Listing specialist · Phoenix',
    },
    es: {
      eyebrow:'ESTRATEGIA DE PRECIO · PREPARADO EL ' + dateEs,
      k1:'PRECIO DE LISTA RECOMENDADO', range:'Rango', confLabel:'Confianza', conf:'Alta',
      how:'CÓMO LLEGAMOS AQUÍ',
      lensA:'Enfoque de avalúo',
      lensAtext:'Ajustado por pies cuadrados, lote, año de construcción y condición contra catorce ventas cerradas.',
      lensB:'Enfoque de broker',
      lensBtext:'Siete pendientes y doce UCB indican que esta zona se está moviendo. Todo lo que superó $632,000 tardó 41 días.',
      obj:'"¿Por qué no $650,000?"',
      objText:'Tres casas lo intentaron en esta zona desde marzo. Dos bajaron el precio en seis semanas; una sigue sin venderse. Sobrevalorar aquí cuesta unos $9,000 en gastos y una segunda mirada más débil.',
      role:'Especialista en listados · Phoenix',
    },
  }
  const t = T[lang]
  const fade = { opacity: fading ? 0 : 1 }

  const TOOLS = [
    { name:'Inspection desk', sub:'BINSR drafting', hb:'#FAEEDA', ht:'#633806', hs:'#854F0B',
      desc:'Paste the report. Get a BINSR draft, ready for your review.' },
    { name:'Deal desk', sub:'Contracts and forms', hb:'#EFE6F7', ht:'#46186B', hs:'#534AB7',
      desc:'A counter, drafted on the actual Arizona forms, ready for your review.' },
    { name:'Pipeline', sub:'Your database, working', hb:'#DCEEEC', ht:'#0B4A44', hs:'#0F6E56',
      desc:'Who to call today, plus notes and tasks, from one place connected to your CRM.' },
    { name:'Ask Ada', sub:'Arizona license law', hb:'#EAF3DE', ht:'#27500A', hs:'#3B6D11',
      desc:'Legal questions, answered from the actual Arizona real estate law.' },
  ]

  const PILLS = [
    { label:'My wins, auto-built',
      path:'<path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 4h10v5a5 5 0 0 1-10 0z"/><path d="M17 5h3v2a3 3 0 0 1-3 3"/><path d="M7 5H4v2a3 3 0 0 0 3 3"/>' },
    { label:'Your link page',
      path:'<path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7L12 19"/>' },
    { label:'Open house funnels',
      path:'<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3v3h-3z"/><path d="M19 19h2"/><path d="M19 14h2v2"/>' },
    { label:'Weekly content, EN + ES',
      path:'<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 9h10M7 13h10M7 17h6"/>' },
    { label:'CRM connected',
      path:'<path d="M4 4h16v12H5.5L4 18z"/><path d="M8 9h8"/><path d="M8 12h5"/>' },
  ]

  const compCard = c => (
    <div key={c.addr} style={{border:'0.5px solid #e8e8e8',borderRadius:'8px',overflow:'hidden',background:'white'}}>
      <div style={{position:'relative',height:'86px',background:'#e8e4dd'}}>
        <img src={c.src} alt="" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
        <span style={{position:'absolute',top:'6px',left:'6px',background:'white',color:'#666',borderRadius:'999px',padding:'2px 8px',fontSize:'9px',fontWeight:600}}>Closed</span>
        <span style={{position:'absolute',top:'6px',right:'6px',background:'#1d2e42',color:'white',borderRadius:'999px',padding:'2px 8px',fontSize:'9px',fontWeight:600}}>{c.dom}</span>
      </div>
      <div style={{padding:'8px 10px'}}>
        <div style={{fontSize:'11px',color:'#1d2e42'}}>{c.addr}</div>
        <div style={{fontSize:'10px',color:'#999',margin:'2px 0 4px'}}>{c.specs}</div>
        <div style={{fontSize:'13px',fontWeight:600,color:'#147efb'}}>{c.price}</div>
      </div>
    </div>
  )

  const bigLine = (text, color) => (
    <div style={{fontSize:'clamp(30px, 5.4vw, 50px)',fontWeight:500,color,letterSpacing:'-1.6px',lineHeight:1.04}}>{text}</div>
  )

  return (
    <div style={{minHeight:'100vh',background:'#f5f5f5',fontFamily:FONT_BODY}}>
      <style>{demoCSS}</style>

      {/* NAV */}
      <div style={{background:'#f5f5f5',borderBottom:'0.5px solid #e8e8e8',position:'sticky',top:0,zIndex:50}}>
        <div style={{...wrap,display:'flex',alignItems:'center',justifyContent:'space-between',height:'54px',gap:'12px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'9px',minWidth:0}}>
            <span style={{color:'#fdbf00',fontSize:'16px',lineHeight:1}}>&#9654;</span>
            <span style={{fontSize:'14px',fontWeight:900,letterSpacing:'1.5px',color:'#1d2e42',fontFamily:FONT_HEAD,whiteSpace:'nowrap'}}>TrueTopProducer</span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:isMobile?'10px':'18px',minWidth:0}}>
            {!isMobile && (
              <button onClick={()=>goTo('appointment')} style={{background:'none',border:'none',padding:0,fontSize:'12.5px',color:'#666',fontFamily:FONT_BODY,cursor:'pointer',whiteSpace:'nowrap'}}>What you will see</button>
            )}
            <a href={BOOK} target="_blank" rel="noopener noreferrer"
              style={{background:'#147efb',color:'white',borderRadius:'999px',padding:'7px 15px',fontSize:'12.5px',fontWeight:600,fontFamily:FONT_BODY,textDecoration:'none',whiteSpace:'nowrap',flexShrink:0}}>
              Book a walkthrough
            </a>
          </div>
        </div>
      </div>

      {/* HERO */}
      <div style={{...wrap,paddingTop:'44px',textAlign:'center'}}>
        <div style={{display:'inline-flex',alignItems:'center',gap:'7px',background:'white',border:'0.5px solid #e8e8e8',borderRadius:'999px',padding:'5px 13px',marginBottom:'20px'}}>
          <span className="dm-dot" style={{width:'6px',height:'6px',borderRadius:'50%',background:'#e23744',display:'inline-block',animation:'dmPulse 1.6s ease-in-out infinite'}} />
          <span style={{fontSize:'11px',color:'#666'}}>Live MLS data · Phoenix, Arizona</span>
        </div>
        {bigLine('See the future of the Arizona deal.', '#1d2e42')}
        {bigLine('Then keep six hours of CE.', '#147efb')}
        <div style={{fontSize:'15px',color:'#666',maxWidth:'420px',margin:'20px auto 0',lineHeight:1.6}}>
          A live twenty-minute walkthrough. Tell us honestly what works and what does not. The CE is our thanks for your time.
        </div>
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'18px',flexWrap:'wrap',marginTop:'24px'}}>
          <a href={BOOK} target="_blank" rel="noopener noreferrer" style={goldPill}>Book your walkthrough</a>
          <button onClick={()=>goTo('appointment')} style={{background:'none',border:'none',padding:0,fontSize:'13px',fontWeight:600,color:'#1d2e42',fontFamily:FONT_BODY,cursor:'pointer'}}>
            See what you will see →
          </button>
        </div>
      </div>

      {/* PRODUCT SHOT */}
      <div style={{...wrap,paddingTop:'40px'}}>
        <div style={{background:'white',border:'0.5px solid #dedede',borderRadius:'14px 14px 0 0',boxShadow:'0 -1px 40px rgba(29,46,66,0.10)',overflow:'hidden'}}>

          <div style={{padding:'10px 14px',background:'#fafafa',borderBottom:'0.5px solid #e8e8e8',display:'flex',alignItems:'center',gap:'6px'}}>
            {[0,1,2].map(i => <span key={i} style={{width:'9px',height:'9px',borderRadius:'50%',background:'#dcdcdc',flexShrink:0}} />)}
            <div style={{flex:1,textAlign:'center',fontSize:'10.5px',color:'#aaa',fontFamily:FONT_BODY}}>truetopproducer.com</div>
            <div style={{width:'33px',flexShrink:0}} />
          </div>

          <div style={{display:'flex',minHeight:'290px'}}>

            {!isMobile && (
              <div style={{width:'152px',flexShrink:0,background:'#f4f4f2',borderRight:'0.5px solid #e8e8e8',padding:'12px 9px'}}>
                <div style={{fontSize:'8.5px',fontWeight:700,letterSpacing:'1px',color:'#999',fontFamily:FONT_HEAD,marginBottom:'5px'}}>YOUR TEAM</div>
                <div style={{background:'white',borderRadius:'6px',padding:'5px 7px',fontSize:'11px',color:'#1d2e42',marginBottom:'2px'}}>The Strategist</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Inspection desk</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Deal desk</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Pipeline</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Ask Ada</div>
                <div style={{fontSize:'8.5px',fontWeight:700,letterSpacing:'1px',color:'#999',fontFamily:FONT_HEAD,margin:'12px 0 5px'}}>BROKERAGE</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Content</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Events</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>My wins</div>
                <div style={{padding:'5px 7px',fontSize:'11px',color:'#666'}}>Link page</div>
              </div>
            )}

            <div style={{flex:1,minWidth:0,padding:'14px 16px'}}>
              <div style={{display:'flex',alignItems:'baseline',justifyContent:'space-between',gap:'10px',marginBottom:'10px'}}>
                <div style={{fontSize:'14px',fontWeight:500,color:'#1d2e42',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>3601 W Tuckey Ln</div>
                <div style={{fontSize:'11px',color:'#999',whiteSpace:'nowrap',flexShrink:0}}>Single family · Seller · 44 comps</div>
              </div>

              <div style={{background:'#1d2e42',borderRadius:'8px',padding:'9px 11px',marginBottom:'11px',position:'relative',overflow:'hidden'}}>
                <div className="dm-sweep" style={{position:'absolute',top:0,left:0,width:'70px',height:'2px',background:'#147efb',animation:'dmSweep 2.8s linear infinite'}} />
                <div style={{fontSize:'8.5px',fontWeight:700,letterSpacing:'1px',color:'#fdbf00',fontFamily:FONT_HEAD,marginBottom:'6px'}}>SUBJECT</div>
                <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
                  <div style={{background:'white',borderRadius:'5px',padding:'5px 9px',fontSize:'10px',color:'#1d2e42',minWidth:'86px'}}>7060254</div>
                  <div style={{display:'flex',alignItems:'flex-end',gap:'2px',height:'13px',flexShrink:0}}>
                    {['0s','.16s','.32s'].map((d,i)=>(
                      <span key={i} className="dm-bar" style={{width:'3px',height:'100%',borderRadius:'2px',background:'#e23744',transformOrigin:'bottom',display:'inline-block',animation:'dmBar 1.1s ease-in-out infinite',animationDelay:d}} />
                    ))}
                  </div>
                  <div style={{background:'#fdbf00',color:'#412402',borderRadius:'5px',padding:'5px 11px',fontSize:'10px',fontWeight:600}}>Pull comps</div>
                </div>
              </div>

              <div style={{fontSize:'9px',fontWeight:700,letterSpacing:'1px',color:'#999',fontFamily:FONT_HEAD,marginBottom:'7px'}}>CLOSED · 2</div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'9px'}}>
                {[
                  { src:'/hero-glass.png', addr:'2920 W Glass Ln',  specs:'1,874 sf · 4bd/3ba · 2006', price:'$590,000', dom:'28 DOM' },
                  { src:'/hero-laura.png', addr:'22606 W Laura St', specs:'3,010 sf · 4bd/3ba · 2025', price:'$710,000', dom:'41 DOM' },
                ].map(compCard)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 01 · THE LISTING APPOINTMENT */}
      <div id="appointment" style={{...wrap,paddingTop:'48px',scrollMarginTop:'62px'}}>
        {sectionLabel('01')}
        <div style={{fontSize:isMobile?'30px':'40px',fontWeight:500,color:'#1d2e42',lineHeight:1.05,letterSpacing:'-1px'}}>The listing appointment.</div>
        <div style={{fontSize:isMobile?'30px':'40px',fontWeight:500,color:'#147efb',lineHeight:1.05,letterSpacing:'-1px',marginBottom:'16px'}}>You have one at 6:00 PM.</div>
        <div style={{fontSize:'15px',color:'#666',lineHeight:1.6,maxWidth:'480px',marginBottom:'26px'}}>
          The address came in at 4:40. This is what the Strategist puts in your hand before you find your keys. All of it from live MLS data. None of it from a template.
        </div>

        <div ref={priceRef} style={{background:'#1d2e42',borderRadius:'14px',padding:isMobile?'24px 20px':'30px 30px 26px'}}>
          <div style={{display:'flex',flexDirection:isMobile?'column':'row',justifyContent:'space-between',alignItems:'flex-start',gap:'22px'}}>
            <div>
              <div style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.4px',color:'#fdbf00',fontFamily:FONT_HEAD,marginBottom:'10px'}}>RECOMMENDED LIST PRICE</div>
              <div style={{fontSize:isMobile?'46px':'58px',fontWeight:500,color:'white',lineHeight:1,letterSpacing:'-1.5px',fontVariantNumeric:'tabular-nums'}}>
                ${price.toLocaleString('en-US')}
              </div>
              <div style={{fontSize:'13px',color:'#B5D4F4',marginTop:'10px'}}>Defensible range $598,000 – $632,000</div>
            </div>
            <div style={{width:isMobile?'100%':'260px',flexShrink:0}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:'11px',color:'#B5D4F4',marginBottom:'8px'}}>
                <span>Confidence</span><span style={{color:'#fdbf00',fontWeight:600}}>High</span>
              </div>
              <div style={{height:'8px',background:'rgba(181,212,244,0.18)',borderRadius:'4px',overflow:'hidden'}}>
                <div className="dm-conf" style={{height:'100%',background:'#fdbf00',borderRadius:'4px',width:priceIn?'82%':'0%',transition:'width 1.4s cubic-bezier(.2,.8,.2,1) .5s'}} />
              </div>
              <div style={{fontSize:'11px',color:'#B5D4F4',marginTop:'10px',lineHeight:1.55}}>Fourteen closed sales inside a half mile in the last six months. Tight spread. This number will hold.</div>
            </div>
          </div>

          <div style={{borderTop:'0.5px solid rgba(181,212,244,0.2)',marginTop:'24px',paddingTop:'20px'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:'12px',flexWrap:'wrap',gap:'6px'}}>
              <div style={{fontSize:'10px',fontWeight:700,letterSpacing:'1.4px',color:'#B5D4F4',fontFamily:FONT_HEAD}}>44 COMPS · BY STATUS</div>
              <div style={{fontSize:'11px',color:'#B5D4F4'}}>pulled in 9 seconds</div>
            </div>
            <div style={{display:'flex',height:'34px',borderRadius:'7px',overflow:'hidden',gap:'2px'}}>
              {BUCKETS.map((b,i) => (
                <div key={b.label} className="dm-seg" style={{
                  width: priceIn ? b.w + '%' : '0%',
                  transition: 'width 1.1s cubic-bezier(.2,.8,.2,1) ' + (i * 0.15) + 's',
                  background:b.bg,color:b.fg,display:'flex',alignItems:'center',paddingLeft:'10px',
                  fontSize:'11px',fontWeight:500,whiteSpace:'nowrap',overflow:'hidden',boxSizing:'border-box'
                }}>{b.label}</div>
              ))}
            </div>
            <div style={{fontSize:'12px',color:'#B5D4F4',marginTop:'12px',lineHeight:1.6,maxWidth:'560px'}}>
              Closed is where the market has been. Pending and UCB are where it's going. You walk in knowing both.
            </div>
          </div>
        </div>
      </div>

      {/* 02 · WHAT THE SELLER SEES */}
      <div style={{...wrap,paddingTop:'44px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end',flexWrap:'wrap',gap:'14px',marginBottom:'18px'}}>
          <div style={{flex:1,minWidth:'260px'}}>
            {sectionLabel('02','WHAT THE SELLER SEES')}
            <div style={{fontSize:isMobile?'28px':'34px',fontWeight:500,color:'#1d2e42',lineHeight:1.08,letterSpacing:'-0.8px'}}>Two brains.</div>
            <div style={{fontSize:isMobile?'28px':'34px',fontWeight:500,color:'#147efb',lineHeight:1.08,letterSpacing:'-0.8px',marginBottom:'10px'}}>One number.</div>
            <div style={{fontSize:'14px',color:'#666',lineHeight:1.6,maxWidth:'460px'}}>
              Reasons like an appraiser. Reads the room like a twenty-year broker. Hands you a report with your name on it, in whichever language the kitchen table speaks.
            </div>
          </div>
          <div style={{display:'flex',background:'white',border:'0.5px solid #e8e8e8',borderRadius:'999px',padding:'3px',flexShrink:0}}>
            {[['en','English'],['es','Español']].map(([k,label]) => (
              <button key={k} onClick={()=>flip(k)} style={{
                background: lang===k ? '#1d2e42' : 'none', color: lang===k ? 'white' : '#666',
                border:'none',borderRadius:'999px',padding:'7px 15px',fontSize:'12px',fontWeight:600,fontFamily:FONT_BODY,cursor:'pointer'
              }}>{label}</button>
            ))}
          </div>
        </div>

        <div ref={reportRef} style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',overflow:'hidden',maxWidth:'560px'}}>

          <div className={rv} style={{animationDelay:'0.1s',background:'#1d2e42',padding:'20px 24px'}}>
            <div className="dm-fade" style={{...fade,fontSize:'9px',fontWeight:700,letterSpacing:'1.4px',color:'#fdbf00',fontFamily:FONT_HEAD,marginBottom:'8px'}}>{t.eyebrow}</div>
            <div style={{fontSize:'21px',fontWeight:500,color:'white',lineHeight:1.2}}>3601 W Tuckey Ln</div>
            <div style={{fontSize:'12px',color:'#B5D4F4',marginTop:'3px'}}>Phoenix, Arizona 85015</div>
          </div>

          <div className={rv} style={{animationDelay:'0.5s',padding:'18px 24px 6px',display:'flex',alignItems:'baseline',gap:'14px',flexWrap:'wrap'}}>
            <div>
              <div className="dm-fade" style={{...fade,fontSize:'9px',fontWeight:700,letterSpacing:'1.2px',color:'#999',fontFamily:FONT_HEAD,marginBottom:'4px'}}>{t.k1}</div>
              <div style={{fontSize:'30px',fontWeight:500,color:'#1d2e42',letterSpacing:'-0.8px'}}>$615,000</div>
            </div>
            <div className="dm-fade" style={{...fade,fontSize:'12px',color:'#666'}}>
              {t.range} $598,000 – $632,000 · {t.confLabel} <span style={{color:'#1d2e42',fontWeight:600}}>{t.conf}</span>
            </div>
          </div>

          <div style={{padding:'14px 24px 0'}}>
            <div className={rv} style={{animationDelay:'0.9s'}}>
              <div className="dm-fade" style={{...fade,fontSize:'9px',fontWeight:700,letterSpacing:'1.2px',color:'#999',fontFamily:FONT_HEAD,marginBottom:'10px'}}>{t.how}</div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:'10px'}}>
              <div className={rv} style={{animationDelay:'1.05s',background:'#f4f4f2',borderRadius:'10px',padding:'12px 14px'}}>
                <div style={{display:'flex',alignItems:'center',gap:'6px',marginBottom:'6px'}}>
                  <span style={{width:'7px',height:'7px',borderRadius:'50%',background:'#147efb',display:'inline-block',flexShrink:0}} />
                  <span className="dm-fade" style={{...fade,fontSize:'11px',fontWeight:600,color:'#1d2e42'}}>{t.lensA}</span>
                </div>
                <div className="dm-fade" style={{...fade,fontSize:'11.5px',color:'#666',lineHeight:1.55}}>{t.lensAtext}</div>
                <div style={{fontSize:'15px',fontWeight:500,color:'#1d2e42',marginTop:'8px'}}>$611,000</div>
              </div>
              <div className={rv} style={{animationDelay:'1.25s',background:'#f4f4f2',borderRadius:'10px',padding:'12px 14px'}}>
                <div style={{display:'flex',alignItems:'center',gap:'6px',marginBottom:'6px'}}>
                  <span style={{width:'7px',height:'7px',borderRadius:'50%',background:'#fdbf00',display:'inline-block',flexShrink:0}} />
                  <span className="dm-fade" style={{...fade,fontSize:'11px',fontWeight:600,color:'#1d2e42'}}>{t.lensB}</span>
                </div>
                <div className="dm-fade" style={{...fade,fontSize:'11.5px',color:'#666',lineHeight:1.55}}>{t.lensBtext}</div>
                <div style={{fontSize:'15px',fontWeight:500,color:'#1d2e42',marginTop:'8px'}}>$615,000</div>
              </div>
            </div>
          </div>

          <div className={rv} style={{animationDelay:'1.6s',margin:'14px 24px 0',borderLeft:'2px solid #fdbf00',padding:'8px 14px',background:'#FFFBEB',borderRadius:'0 8px 8px 0'}}>
            <div className="dm-fade" style={{...fade,fontSize:'12px',fontWeight:600,color:'#1d2e42',marginBottom:'3px'}}>{t.obj}</div>
            <div className="dm-fade" style={{...fade,fontSize:'11.5px',color:'#666',lineHeight:1.55}}>{t.objText}</div>
          </div>

          <div className={rv} style={{animationDelay:'2s',margin:'16px 24px 0',borderTop:'0.5px solid #e8e8e8',padding:'14px 0 20px',display:'flex',alignItems:'center',gap:'12px'}}>
            <div style={{width:'40px',height:'40px',borderRadius:'50%',background:'#E6F1FB',border:'0.5px solid #B5D4F4',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'12px',fontWeight:600,color:'#0C447C',flexShrink:0}}>JA</div>
            <div>
              <div style={{fontSize:'12.5px',fontWeight:600,color:'#1d2e42'}}>Josh Andreti</div>
              <div className="dm-fade" style={{...fade,fontSize:'11px',color:'#999'}}>{t.role}</div>
            </div>
          </div>

        </div>
        <div style={{fontSize:'11px',color:'#999',marginTop:'12px'}}>Illustrative figures. Names and numbers are marketing copy, not a live pull. Every report is a draft for the agent to review.</div>
      </div>

      {/* 03 · THEN THE DEAL STARTS */}
      <div style={{...wrap,paddingTop:'44px'}}>
        {sectionLabel('03','THEN THE DEAL STARTS')}
        {sectionHead('The Strategist wins the listing. These carry it to closing.')}
        <div style={{fontSize:'13px',color:'#666',lineHeight:1.6,maxWidth:'470px',marginTop:'-8px',marginBottom:'16px'}}>
          Same login. We'll walk these in the second ten minutes.
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:'12px',marginBottom:'14px'}}>
          {TOOLS.map(sp => (
            <div key={sp.name} style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',overflow:'hidden'}}>
              <div style={{background:sp.hb,padding:'11px 13px'}}>
                <div style={{fontSize:'13px',fontWeight:600,color:sp.ht}}>{sp.name}</div>
                <div style={{fontSize:'11px',color:sp.hs,marginTop:'2px'}}>{sp.sub}</div>
              </div>
              <div style={{padding:'11px 13px 13px',fontSize:'11.5px',color:'#666',lineHeight:1.5}}>{sp.desc}</div>
            </div>
          ))}
        </div>
        <div style={{display:'flex',flexWrap:'wrap',gap:'8px'}}>
          {PILLS.map(p => (
            <span key={p.label} style={{display:'inline-flex',alignItems:'center',gap:'6px',background:'white',border:'0.5px solid #e8e8e8',borderRadius:'999px',padding:'7px 13px',fontSize:'11.5px',color:'#1d2e42'}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#147efb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" dangerouslySetInnerHTML={{__html:p.path}} />
              {p.label}
            </span>
          ))}
        </div>
      </div>

      {/* 04 · THE CE OFFER */}
      <div style={{...wrap,paddingTop:'48px',textAlign:'center'}}>
        <div style={{display:'flex',justifyContent:'center'}}>{sectionLabel('04','THE CE OFFER')}</div>
        <div style={{fontSize:isMobile?'26px':'30px',fontWeight:500,color:'#1d2e42',lineHeight:1.15,marginTop:'8px'}}>Twenty minutes with us.</div>
        <div style={{fontSize:isMobile?'26px':'30px',fontWeight:500,color:'#147efb',lineHeight:1.15,marginBottom:'18px'}}>Six hours of CE for you.</div>
        <div style={{fontSize:'14px',color:'#666',lineHeight:1.65,maxWidth:'440px',margin:'0 auto 26px'}}>
          Two full Arizona classes through Avance School of Real Estate, ADRE-approved. Sit through the walkthrough, tell us what you honestly think, and they're covered.
        </div>
        <a href={BOOK} target="_blank" rel="noopener noreferrer" style={{...goldPill,padding:'13px 28px',fontSize:'14px'}}>Book your walkthrough</a>
        <div style={{fontSize:'12px',color:'#999',marginTop:'14px'}}>Live, screen shared, with the person who runs the platform.</div>
      </div>

      {/* FOOTER */}
      <div style={{...wrap,paddingTop:'48px',paddingBottom:'48px'}}>
        <div style={{borderTop:'0.5px solid #e8e8e8',paddingTop:'18px',textAlign:'center'}}>
          <div style={{fontSize:'10px',letterSpacing:'2px',color:'#999',fontFamily:FONT_HEAD,marginBottom:'14px'}}>
            TRUETOPPRODUCER · PHOENIX, AZ
          </div>
          {/* The old wrapper carried margin:'0 auto 12px'. SchoolDisclosure owns
              its own '0 auto' centering, so only the 12px bottom gap to the
              attribution line below needs restating here. */}
          <div style={{marginBottom:'12px'}}>
            <SchoolDisclosure />
          </div>
          <div style={{fontSize:'10.5px',color:'#999',lineHeight:1.6,maxWidth:'430px',margin:'0 auto 14px'}}>
            TrueTopProducer is a product of HomePros Arizona. CE credit issued through Avance School of Real Estate.
          </div>
          <a href="/" style={{fontSize:'12px',color:'#147efb',textDecoration:'none',fontWeight:600}}>See the full platform →</a>
        </div>
      </div>
    </div>
  )
}
